import cloneDeep from 'lodash/cloneDeep';
import isNull from 'lodash/isNull';

import {
  LimitMessages,
  LimitTitles,
} from '@qality/common-components/src/molecules/QalityFreeLimitsBanners/QalityFreeLimitsBanner';
import { TEST_EXECUTIONS_LIMIT_REACHED_CODE } from '@qality/common-components/src/test-case-box/ExecutionDialog/ExecutionDialog';
import { FEATURE_FLAG } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import {
  ExecutionStatus,
  TestCaseInCycle,
  TestCycle,
} from '@qality/common-model';
import { UserOption } from '@qality/common-model/src/UserOption';
import {
  FlagService,
  StorageOfUniqueServices,
  TestCyclesService,
  TestExecutionService,
} from '@qality/common-service';
import Utils from '@qality/common-utils/src/Utils';

export async function handleExecute(
  testCaseInCycle: TestCaseInCycle
): Promise<void> {
  if (testCaseInCycle.testCase) {
    let testExecutionId;

    if (isNull(testCaseInCycle.testExecution)) {
      try {
        // create new test execution
        const testExecution = await TestExecutionService.createTestExecution(
          testCaseInCycle.testCase.fields.summary,
          testCaseInCycle.testCaseId,
          testCaseInCycle.id
        );
        testExecutionId = testExecution.id;
      } catch (e) {
        if (e?.response?.status === 409) {
          // executed by someone else in the meantime
          const testCaseInCycleNew =
            await TestCyclesService.fetchTestCaseInCycle(
              testCaseInCycle.id,
              testCaseInCycle.testCycle?.id
            );
          testExecutionId = testCaseInCycleNew.testExecution.id;
        } else if (e?.response?.status === TEST_EXECUTIONS_LIMIT_REACHED_CODE) {
          FlagService.createErrorFlag(
            LimitTitles['test-case-executions'],
            LimitMessages['test-case-executions']
          );
          throw e;
        } else {
          StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
            e,
            messages['error.executeTestCase.message']
          );
          return;
        }
      }
    } else {
      // existing test exection id
      testExecutionId = testCaseInCycle.testExecution.id;
    }

    const url = await StorageOfUniqueServices.get(
      'URLService'
    ).getTestCaseExecutionUrl(testExecutionId);
    StorageOfUniqueServices.get('AP').navigator.go('site', {
      absoluteUrl: url,
    });
  }
}

export async function handleExecutionAssigneeChange(
  testCaseInCycle: TestCaseInCycle,
  newExecutionAssignee: UserOption,
  updateTestCycle: (testCycleToUpdate: TestCycle) => void,
  testCycle: TestCycle
): Promise<void> {
  try {
    let testExecution = null;
    if (Utils.isNilOrEmpty(testCaseInCycle.testExecution)) {
      testExecution = await TestExecutionService.createTestExecution(
        testCaseInCycle.testCase.fields.summary,
        testCaseInCycle.testCase.id,
        testCaseInCycle.id,
        newExecutionAssignee.user
      );
    } else {
      testExecution = await TestExecutionService.updateExecutionAssignee(
        testCaseInCycle.testExecution.id,
        newExecutionAssignee.value
      );
    }

    testExecution.executionAssignee = newExecutionAssignee.user;
    const updatedTestCycle = cloneDeep(testCycle);

    updatedTestCycle.testCases.forEach((testCase) => {
      if (testCase.id === testCaseInCycle.id) {
        testCase.testExecution = testExecution;
      }
    });
    updateTestCycle(updatedTestCycle);
  } catch (e) {
    const message =
      e.response?.data?.message ||
      messages['error.testCaseExecutionPage.updateTestExecutionStatus.message'];
    FlagService.createErrorFlag('Error', message);
  }
}

export async function handleExecutionStatusChange(
  testCaseInCycle: TestCaseInCycle,
  newExecutionStatus: ExecutionStatus,
  updateTestCycle: (testCycleToUpdate: TestCycle) => void,
  testCycle: TestCycle
): Promise<void> {
  try {
    const testExecution = Utils.isNilOrEmpty(testCaseInCycle.testExecution)
      ? await TestExecutionService.createTestExecution(
          testCaseInCycle.testCase.fields.summary,
          testCaseInCycle.testCase.id,
          testCaseInCycle.id
        )
      : cloneDeep(testCaseInCycle.testExecution);

    const requestData = {
      testExecutions: [
        {
          executionStatus: newExecutionStatus,
          testExecutionId: testExecution.id,
        },
      ],
    };

    await TestExecutionService.bulkUpdateExecutionsStatus(requestData);

    testExecution.status = newExecutionStatus;
    const updatedTestCycle = cloneDeep(testCycle);

    updatedTestCycle.testCases.forEach((testCase) => {
      if (testCase.id === testCaseInCycle.id) {
        testCase.testExecution = testExecution;
        testCase.statusDetail = newExecutionStatus;
      }
    });
    updateTestCycle(updatedTestCycle);
  } catch (e) {
    const message =
      e.response?.data?.message ||
      'We could not update the status in the test execution. Please try again. If the problem persists, feel free to contact our support.';
    FlagService.createErrorFlag('Error', message);
  }
}

export function canEditExecutionAssignee(
  canEdit: boolean,
  testCaseInCycle: TestCaseInCycle
): boolean {
  return FEATURE_FLAG.isPremium && canEdit && !!testCaseInCycle.testCase;
}
